import React, { FC, useState } from 'react'
import { DayPicker, CaptionProps, useNavigation, DayContentProps } from 'react-day-picker'
import { ButtonGroup, Classes, Intent, Popover, Position } from '@blueprintjs/core'
// import moment from 'moment'
import { DateUtil, TIME } from '@utils'
import { Callout, IconButton } from '..'
import CustomCheckbox from '../CustomCheckbox'
import MonthSelect, { months, Month } from '../selects/MonthSelect'
import YearSelect from '../selects/YearSelect'
import { groupBy } from 'lodash'
import useCalendarEvents, {
  CalendarEvent,
  IEventType,
  MoveEvent,
  NotificationEvent,
  ExpirationEvent,
} from './useCalendarEvents'
import track from '@track'
import 'react-day-picker/dist/style.css'
import css from './eventCalendar.module.scss'

interface EventCalendarProps {
  city: string | null
  showLegend?: boolean
  notifications?: boolean
  expirations?: boolean
  movePlans?: boolean
  onDateSelect?: (date: Date) => void
  minDate?: Date
}

const formatDate = (d: Date | string) => DateUtil.utc(d).startOf(TIME.DAYS).toISOString()

export default function EventCalendar({
  city,
  minDate,
  showLegend = true,
  notifications = true,
  expirations = true,
  movePlans = true,
  onDateSelect = () => null,
}: EventCalendarProps) {
  const [month, setMonth] = useState(new Date())
  const [showNotifications, setShowNotifications] = useState(notifications)
  const [showExpirations, setShowExpirations] = useState(expirations)
  const [showMoves, setShowMoves] = useState(movePlans)

  const { events } = useCalendarEvents({
    month,
    city,
  })

  let evs: { [key: string]: CalendarEvent[] }
  if (events) {
    evs = groupBy(
      events.reduce((result: CalendarEvent[], e: CalendarEvent): CalendarEvent[] => {
        switch (e.eventType) {
          case IEventType.MOVE:
            return showMoves
              ? [
                  ...result,
                  {
                    ...e,
                    date: formatDate(e.date),
                  },
                ]
              : result
          case IEventType.NOTIFICATION:
            return showNotifications
              ? [
                  ...result,
                  {
                    ...e,
                    date: formatDate(e.date),
                  },
                ]
              : result
          case IEventType.EXPIRATION:
            return showExpirations
              ? [
                  ...result,
                  {
                    ...e,
                    date: formatDate(e.date),
                  },
                ]
              : result
          default:
            return result
        }
      }, []),
      'date'
    )
  }

  return (
    <>
      <DayPicker
        className="EventCalendar"
        disabled={(day: Date) => (minDate ? DateUtil(day).isBefore(minDate) : false)}
        month={month}
        modifiersStyles={{ outside: { background: 'var(--white)' } }}
        onMonthChange={(month: Date) => setMonth(month)}
        components={{
          Caption: (props: CaptionProps) => <CustomCaption setMonth={setMonth} {...props} />,
          DayContent: ({ date }: DayContentProps) => (
            <Day
              day={date}
              events={evs && evs[formatDate(date)]}
              onClick={() => {
                if (
                  evs && !evs[formatDate(date)] && minDate ? DateUtil(date).isAfter(minDate) : true
                ) {
                  onDateSelect(date)
                }
              }}
            />
          ),
        }}
      />
      {showLegend && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CustomCheckbox
            intent={Intent.SUCCESS}
            label="Move Plan"
            style={{ marginRight: 25 }}
            checked={showMoves}
            onChange={() => setShowMoves(!showMoves)}
          />
          <CustomCheckbox
            label="Lease Notification"
            intent={Intent.WARNING}
            style={{ marginRight: 25 }}
            checked={showNotifications}
            onChange={() => setShowNotifications(!showNotifications)}
          />
          <CustomCheckbox
            label="Lease Expiration"
            intent={Intent.DANGER}
            checked={showExpirations}
            onChange={() => setShowExpirations(!showExpirations)}
          />
        </div>
      )}
    </>
  )
}

function YearMonthForm({
  date,
  onChange,
}: {
  date: Date
  localeUtils?: any
  onChange: (date: Date) => void
}) {
  const selectedMonth: Month = months.find(({ value }) => value === date.getMonth()) as Month

  function handleChange({ year, month }: { year?: number; month?: number }) {
    const _year = year || date.getFullYear()
    const _month = month ?? date.getMonth()
    onChange(new Date(_year, _month))
  }

  return (
    <div className="DayPicker-Caption">
      <span style={{ paddingRight: 7 }}>
        <YearSelect
          buttonProps={{ small: true }}
          value={date.getFullYear()}
          onChange={(year: number) => handleChange({ year })}
        />
      </span>
      <span>
        <MonthSelect
          buttonProps={{ small: true }}
          value={selectedMonth}
          onChange={(item: Month) => handleChange({ month: item.value })}
        />
      </span>
    </div>
  )
}

type CustomCaptionPropsT = CaptionProps & {
  setMonth: (date: Date) => void
}

const CustomCaption: FC<CustomCaptionPropsT> = ({ displayMonth, setMonth }) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation()
  return (
    <div className={css.day_picker__wrapper}>
      <YearMonthForm date={displayMonth} onChange={setMonth} />
      <div className={css.day_picker__nav_bar}>
        <ButtonGroup>
          <IconButton
            small
            round={false}
            disabled={!previousMonth}
            iconName="long-arrow-left"
            onClick={() => previousMonth && goToMonth(previousMonth)}
          />
          <IconButton
            small
            round={false}
            disabled={!nextMonth}
            iconName="long-arrow-right"
            onClick={() => nextMonth && goToMonth(nextMonth)}
          />
        </ButtonGroup>
      </div>
    </div>
  )
}

function Day({
  day,
  events,
  onClick,
}: {
  day: Date
  events?: CalendarEvent[]
  onClick: () => void
}) {
  return events ? (
    <Popover
      position={Position.TOP}
      content={<DayDetails day={day} events={events} />}
      onOpened={() =>
        track.dashboard({
          eventName: track.Event.CALENDAR_DAY_CLICK,
        })
      }>
      <CustomDay day={day} events={events} onClick={onClick} />
    </Popover>
  ) : (
    <CustomDay day={day} onClick={onClick} />
  )
}

function CustomDay({
  day,
  events,
  onClick,
}: {
  day: Date
  events?: CalendarEvent[]
  onClick: () => void
}) {
  const e = groupBy(events, 'eventType')
  return (
    <div style={{ cursor: 'pointer' }} onClick={onClick}>
      {day.getDate()}
      <div className={css.events}>
        {e[IEventType.MOVE] && <div className={css.dot__move_plan} />}
        {e[IEventType.NOTIFICATION] && <div className={css.dot__lease_notification} />}
        {e[IEventType.EXPIRATION] && <div className={css.dot__lease_expiration} />}
      </div>
    </div>
  )
}

function DayDetails({ day, events }: { day: Date; events?: CalendarEvent[] }) {
  return (
    <div
      style={{
        padding: 10,
        background: 'white',
        borderRadius: 4,
      }}>
      <div className={css.popover_closeButton}>
        <IconButton minimal iconName="times" className={Classes.POPOVER_DISMISS} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <strong>{DateUtil(day).utc().format('MMM Do, yyyy')}</strong>
      </div>
      <div
        style={{
          maxHeight: 400,
          overflowY: 'auto',
        }}>
        {events ? (
          events.map((event, i) => {
            switch (event.eventType) {
              case IEventType.MOVE:
                return <MoveCallout key={`move-callout-${i}`} event={event} day={day} />
              case IEventType.NOTIFICATION:
                return <NotifyCallout key={`notify-callout-${i}`} event={event} day={day} />
              case IEventType.EXPIRATION:
                return <ExpiryCallout key={`expiry-callout-${i}`} event={event} day={day} />
              default:
                return null
            }
          })
        ) : (
          <Callout title="No events..." faIcon="" />
        )}
      </div>
    </div>
  )
}

function MoveCallout({ day, event }: { day: Date; event: MoveEvent }) {
  return (
    <Callout faIcon="box" style={{ marginBottom: 10 }} title={'Move scheduled'}>
      {`${event.data.moveOrders.counters.nonCanceled} moves`}
    </Callout>
  )
}

function NotifyCallout({ day, event }: { day: Date; event: NotificationEvent }) {
  const date = DateUtil.utc(day)
  const noticeDate = DateUtil.utc(event.data.noticeDate)
  const mosToNotice = date.diff(noticeDate, TIME.MONTHS)

  const isToday = date.diff(noticeDate, TIME.MONTHS) === 0

  const message = isToday
    ? `Today is the notification date for ${event.data.title}`
    : `${mosToNotice} months until notification date for ${event.data.title}`

  return (
    <Callout
      intent={Intent.WARNING}
      faIcon="exclamation-triangle"
      style={{ marginBottom: 10 }}
      title={'Lease Notification'}>
      {message}
    </Callout>
  )
}

function ExpiryCallout({ day, event }: { day: Date; event: ExpirationEvent }) {
  const date = DateUtil.utc(day)
  const expDate = DateUtil.utc(event.data.endDate)
  const mosToExp = date.diff(expDate, TIME.MONTHS)
  const isToday = date.diff(expDate, TIME.MONTHS) === 0

  const message = isToday
    ? `Today is the expiration date for ${event.data.title}`
    : `${mosToExp} months until expiration date for ${event.data.title}`

  return (
    <Callout
      intent={Intent.DANGER}
      faIcon="exclamation-circle"
      style={{ marginBottom: 10 }}
      title="Lease expiration">
      {message}
    </Callout>
  )
}
